
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    Slide,
    TextField,
    Grid,
    Card,
    CardContent,
    LinearProgress
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {ListViewModel,
        ListViewColumnModel,
        RecordModel} from '../../models/models';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import { getWidthDefinition } from  "../../utils/helperDesign";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import Config from "../../config";
import { useAuth } from "../../hooks/useAuth";
import Plot from 'react-plotly.js';
import {KPICardFigureComponent} from '../widgets/KPICardFigure';
import {InfoCardFigureComponent} from '../widgets/InfoCardFigure';
import {FilterComponent} from '../filters/Filter';
import {WidgetType,
        ChartWidget,
        ChartWidgetFactory,
        PlotlyChartFigure,
        KPICardFigure,
        InfoCardFigure,
        ScreenElementChartModel,
        ScreenElementChartFactory} from "../../models/modelsDashboard";
import {FilterFactory,
        FilterJSON,
        FilterModel} from "../../models/modelsFilter";
import { getChart,
         ChartResponseType } from "../../services/chartApi";
import RefreshIcon from '@mui/icons-material/Refresh';

export const ChartComponent: React.FC<{
  data: ScreenElementChartModel,
}> = ({
  data,
}) => {


  const styles = ContainerClasses();

  let {appName, objectName, recordUuid} = useParams();
  const {accessToken, onInvalidToken} = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const [widgets, setWidgets] = useState<ChartWidget[]>(data.widgets);
  const [filters, setFilters] = useState<FilterModel[]>(data.filters);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  useEffect(() => {
    if(errorMessage){
     enqueueSnackbar(errorMessage, {
          variant: "error",
       });
    }
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleFilterChange = ((thisFilter: FilterModel) => {
    const updatedFilters = filters.map((filter: FilterModel) =>
             filter.id === thisFilter.id
               ? thisFilter
               : filter
            )
    setIsFetching(true)
    setErrorMessage(null)

    getChart(accessToken, data.Uuid_sys, updatedFilters)
         .then((data: ChartResponseType) => {
           const chartScreen = data.data

           const thisWidgets = chartScreen.widgets.map((thisWidget: any) => {
             return thisWidget as any
           })
           setWidgets(thisWidgets)
           const thisFilters = chartScreen.filters.map((thisFilter: FilterModel) => thisFilter)
           setFilters(thisFilters)

           setIsFetching(false)
         })
         .catch((err) => {
           setErrorMessage(err.message)
           setIsFetching(false)
         })
  })

  const refetchChart = () => {
    setIsFetching(true)
    setErrorMessage(null)
    getChart(accessToken, data.Uuid_sys, filters)
         .then((data: ChartResponseType) => {
           const chartScreen = data.data

           const thisWidgets = chartScreen.widgets.map((thisWidget: any) => {
             return thisWidget as any
           })
           setWidgets(thisWidgets)
           const thisFilters = chartScreen.filters.map((thisFilter: FilterModel) => thisFilter)
           setFilters(thisFilters)

           setIsFetching(false)
         })
         .catch((err) => {
           setErrorMessage(err.message)
           setIsFetching(false)
         })
  }


  const renderPlotlyFigure = (figure: any) => {
    return (
      <Card
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Plot
            data={figure.data}
            layout={figure.layout}
            config={figure.config || {responsive: true}}
            style={{ width: '100%',
                     height: '100%'}}
         />
       </Card>
    )
  }

  const renderKPICardFigure = (widget: any) => {
    //user proper typing
    const thisFigure = new KPICardFigure(widget.figure)
    return (
      <KPICardFigureComponent
        figure={thisFigure}
      />
    )
  }

  const renderInfoCardFigure = (widget: any) => {
    //user proper typing
    const thisFigure = new InfoCardFigure(widget.figure)
    return (
      <InfoCardFigureComponent
        figure={thisFigure}
      />
    )
  }


  return (
    <Grid container spacing = {3}>

      <Grid
       item
       xs={12} md={12}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: "hidden",
          gap: 2
        }}>
          {/*This Box is always being shown,
            so no layoutshift when displaying linear progress*/}
          <Box sx={{height: 4}}>
            {isFetching &&
             <LinearProgress />
            }
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
          >
            <IconButton
              size="small"
              onClick={() => {refetchChart()}}
            >
              <RefreshIcon/>
            </IconButton>
          </Box>
        </Box>
      </Grid>


      {filters.length > 0 &&
      <Grid
        item
        xs={12}
        md={12}
      >
        <Card
          elevation={2}
          sx={{borderRadius: 4,
           backgroundColor: 'white',
           border: '1px solid',
           borderColor: 'divider',
           boxShadow: 'none',
           height: '100%'}}
        >
          <CardContent>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                width: '100%',
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "top",
                gap: 4
              }}
            >
              {filters.map((thisFilter: FilterModel) => {
                return (
                  <FilterComponent
                     key={thisFilter.id}
                     filter={thisFilter}
                     onChange={handleFilterChange}
                  />
                )
              })}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    }

    {widgets.map((thisWidget: any) => {
      if(thisWidget.type === 'PlotlyChart'){
        const widthDefinition = getWidthDefinition(thisWidget.width, 12)
        return(
          <Grid
            item
            key={thisWidget.id}
            xs={widthDefinition.xs}
            md={widthDefinition.md}
          >
            {renderPlotlyFigure(thisWidget.figure)}
          </Grid>
        )
      }else if(thisWidget.type === 'KPICard'){
        const widthDefinition = getWidthDefinition(thisWidget.width, 4)
        return (
          <Grid
            item
            key={thisWidget.id}
            xs={widthDefinition.xs}
            md={widthDefinition.md}
          >
            {renderKPICardFigure(thisWidget)}
          </Grid>
        )
      }
      else if(thisWidget.type === 'InfoCard'){
        const widthDefinition = getWidthDefinition(thisWidget.width, 3)
        return (
          <Grid
            item
            key={thisWidget.id}
            xs={widthDefinition.xs}
            md={widthDefinition.md}
          >
            {renderInfoCardFigure(thisWidget)}
          </Grid>
        )
      }
    })}
    </Grid>
  );

};
